import {Box, Stack, Tooltip, Typography, useTheme} from '@mui/material';
import {useRef, useState, useEffect} from 'react';

const STACK_COLORS = [
    '#14b8a6',
    '#f59e0b',
    '#6366f1',
    '#ec4899',
    '#84cc16',
    '#0ea5e9',
];

const SEGMENT_GAP = 2;
const SEGMENT_LABEL_MIN_HEIGHT = 13;

const readableInk = (color: string) => {
    if (!/^#[0-9a-f]{6}$/i.test(color)) return '#fff';
    const channel = (i: number) => {
        const v = parseInt(color.slice(1 + i * 2, 3 + i * 2), 16) / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    };
    const luminance =
        0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
    return luminance > 0.35 ? 'rgba(0, 0, 0, 0.82)' : '#fff';
};

export interface VerticalBarSeries {
    label: string;
    color?: string;
}

export interface VerticalBarGroup {
    label: string;
    values: (number | null)[];
    isCount?: boolean;
    maxOverride?: number;
}

interface VerticalBarChartProps {
    groups: VerticalBarGroup[];
    series: VerticalBarSeries[];
    sharedMax?: boolean;
    barHeight?: number;
    showLegend?: boolean;
    pairGap?: number;
    groupGapRatio?: number;
    stacked?: boolean;
    maxBarWidth?: number;
}

const VerticalBarChart = ({
    groups,
    series,
    sharedMax = true,
    barHeight = 160,
    showLegend = true,
    pairGap = 3,
    groupGapRatio = 0.5,
    stacked = false,
    maxBarWidth = 96,
}: VerticalBarChartProps) => {
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(([entry]) => {
            setContainerWidth(entry.contentRect.width);
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    const AUTO_COLORS = stacked
        ? STACK_COLORS
        : [
              theme.palette.primary.main,
              theme.palette.primary.dark,
              theme.palette.secondary?.main ?? theme.palette.primary.light,
              theme.palette.secondary?.dark ?? theme.palette.primary.light,
          ];

    const resolvedColors = series.map(
        (s, i) => s.color ?? AUTO_COLORS[i % AUTO_COLORS.length],
    );

    const groupTotal = (group: VerticalBarGroup) =>
        group.values.reduce<number>((sum, v) => sum + (v ?? 0), 0);

    const globalMax = Math.max(
        ...(stacked
            ? groups.map(groupTotal)
            : groups.flatMap((g) => g.values.map((v) => v ?? 0))),
        1,
    );

    const fmt = (v: number | null, isCount?: boolean) =>
        v === null ? '—' : isCount ? String(v) : `${v.toFixed(2)}%`;

    const getMax = (group: VerticalBarGroup) => {
        if (group.maxOverride != null) return group.maxOverride;
        if (sharedMax) return globalMax;
        if (stacked) return Math.max(groupTotal(group), 1);
        return Math.max(...group.values.map((v) => v ?? 0), 1);
    };

    const seriesCount = series.length;
    const barsPerGroup = stacked ? 1 : seriesCount;
    const n = groups.length;

    let barWidth = 12;
    let groupGap = 16;
    let slotWidth = barsPerGroup * barWidth + (barsPerGroup - 1) * pairGap;

    if (containerWidth > 0 && n > 0) {
        slotWidth = containerWidth / (n + (n - 1) * groupGapRatio);
        groupGap = slotWidth * groupGapRatio;
        barWidth = Math.max(
            4,
            (slotWidth - (barsPerGroup - 1) * pairGap) / barsPerGroup,
        );
    }

    if (stacked) barWidth = Math.min(barWidth, maxBarWidth);

    return (
        <Box ref={containerRef}>
            {showLegend && (
                <Stack
                    direction='row'
                    spacing={2}
                    sx={{mb: 2, flexWrap: 'wrap'}}
                >
                    {series.map((s, i) => (
                        <Stack
                            key={s.label}
                            direction='row'
                            spacing={0.75}
                            alignItems='center'
                        >
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '2px',
                                    bgcolor: resolvedColors[i],
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant='caption'
                                color='text.secondary'
                            >
                                {s.label}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            )}

            {/* Bars */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: `${groupGap}px`,
                    height: barHeight,
                }}
            >
                {groups.map((group) => {
                    const max = getMax(group);

                    const plotHeight =
                        barHeight -
                        SEGMENT_GAP * Math.max(group.values.length - 1, 0);
                    let stackUsed = 0;
                    const segments = group.values.map((val, si) => {
                        const px = Math.round(
                            Math.min((val ?? 0) / max, 1) * plotHeight,
                        );
                        const height = Math.max(
                            0,
                            Math.min(px, plotHeight - stackUsed),
                        );
                        stackUsed += height;
                        return {si, val, height};
                    });
                    const visibleSegments = segments.filter((s) => s.height > 0);

                    return (
                        <Box
                            key={group.label}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                gap: `${pairGap}px`,
                                height: '100%',
                                flexShrink: 0,
                                width: slotWidth,
                            }}
                        >
                            {stacked ? (
                                <Box
                                    sx={{
                                        width: barWidth,
                                        flexShrink: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        height: '100%',
                                    }}
                                >
                                    <Typography
                                        variant='caption'
                                        sx={{
                                            fontSize: 9,
                                            fontWeight: 500,
                                            color: 'text.secondary',
                                            mb: 0.4,
                                            lineHeight: 1,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {group.values.every((v) => v === null)
                                            ? '—'
                                            : fmt(
                                                  groupTotal(group),
                                                  group.isCount,
                                              )}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: barWidth,
                                            display: 'flex',
                                            flexDirection: 'column-reverse',
                                            gap: `${SEGMENT_GAP}px`,
                                        }}
                                    >
                                        {visibleSegments.map(
                                            ({si, val, height}, vi) => (
                                                <Tooltip
                                                    key={si}
                                                    title={`${series[si]?.label ?? ''}: ${fmt(val, group.isCount)}`}
                                                    placement='top'
                                                    arrow
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height,
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            justifyContent:
                                                                'center',
                                                            bgcolor:
                                                                resolvedColors[
                                                                    si
                                                                ],
                                                            borderRadius:
                                                                vi ===
                                                                visibleSegments.length -
                                                                    1
                                                                    ? '3px 3px 0 0'
                                                                    : 0,
                                                            transition:
                                                                'height 0.3s ease',
                                                        }}
                                                    >
                                                        {height >=
                                                            SEGMENT_LABEL_MIN_HEIGHT && (
                                                            <Typography
                                                                variant='caption'
                                                                sx={{
                                                                    fontSize: 10,
                                                                    fontWeight: 600,
                                                                    lineHeight: 1,
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                    color: readableInk(
                                                                        resolvedColors[
                                                                            si
                                                                        ],
                                                                    ),
                                                                }}
                                                            >
                                                                {fmt(
                                                                    val,
                                                                    group.isCount,
                                                                )}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Tooltip>
                                            ),
                                        )}
                                    </Box>
                                </Box>
                            ) : (
                            group.values.map((val, si) => {
                                const pct =
                                    val !== null
                                        ? Math.min((val / max) * 100, 100)
                                        : 0;
                                const px = Math.round((pct / 100) * barHeight);

                                return (
                                    <Box
                                        key={si}
                                        sx={{
                                            width: barWidth,
                                            flexShrink: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant='caption'
                                            sx={{
                                                fontSize: 9,
                                                fontWeight: 500,
                                                color: resolvedColors[si],
                                                mb: 0.4,
                                                lineHeight: 1,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {fmt(val, group.isCount)}
                                        </Typography>
                                        <Box
                                            sx={{
                                                width: barWidth,
                                                height: px,
                                                minHeight: val !== null ? 2 : 0,
                                                bgcolor: resolvedColors[si],
                                                borderRadius: '3px 3px 0 0',
                                                transition: 'height 0.3s ease',
                                            }}
                                        />
                                    </Box>
                                );
                            })
                            )}
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{borderTop: '1px solid', borderColor: 'divider'}} />

            {/* X-axis labels */}
            <Box sx={{display: 'flex', gap: `${groupGap}px`, mt: 0.75}}>
                {groups.map((group) => (
                    <Typography
                        key={group.label}
                        variant='caption'
                        sx={{
                            width: slotWidth,
                            flexShrink: 0,
                            fontSize: 11,
                            fontWeight: 500,
                            color: 'text.disabled',
                            textAlign: 'center',
                            lineHeight: 1.3,
                        }}
                    >
                        {group.label}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

export default VerticalBarChart;
